import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import QrScanner from './QrScanner';
import { post } from '../api/client';
import { Modal, Button, Field, Input, Badge } from './UI';
import { fmtTime } from '../utils/format';

/**
 * Scanning attendees into one session from the schedule, hands-free: each
 * badge is marked the moment it is read, a green "Done" flashes over the
 * camera, and scanning carries on for the next person.
 *
 * On an event whose check-in form insists on it, the desk is asked how many
 * people are with the guest before that badge counts.
 */
export default function SessionScanModal({ eventId, session, onClose }) {
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);   // { ok, title, sub }
  const [feed, setFeed] = useState([]);
  const [asking, setAsking] = useState(null);   // { code, guest, expected, max, value }
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const show = (f) => {
    clearTimeout(timer.current);
    setFlash(f);
    timer.current = setTimeout(() => setFlash(null), f.ok ? 1400 : 2400);
  };

  const mark = async (code, companions) => {
    setBusy(true);
    try {
      const res = await post('/attendance/scan', {
        code, event: eventId, scanType: 'session', sessionId: session._id,
        ...(companions === undefined ? {} : { companions }),
      });
      const d = res.data || {};
      const name = d.guest?.name || 'Attendee';
      if (d.needsParty) {
        // This event counts the party, and nobody has said what this one is.
        setAsking({ code, guest: d.guest, expected: d.expectedCompanions, max: d.maxCompanions, value: String(d.expectedCompanions ?? 0) });
        return;
      }
      setAsking(null);
      const already = Boolean(d.duplicate);
      const party = d.companions || 0;
      show({ ok: true, title: already ? 'Already marked' : 'Done', sub: party ? `${name} +${party}` : name });
      setFeed((f) => [{ id: Date.now(), name, at: new Date(), already, companions: d.companions, state: d.partyState }, ...f].slice(0, 40));
    } catch (err) {
      show({ ok: false, title: 'Not marked', sub: err.message });
    } finally {
      setBusy(false);
    }
  };

  const onScan = (code) => mark(code);

  const marked = feed.filter((f) => !f.already).length;

  return (
    <Modal open onClose={onClose} width={560} title={`Scan attendees · ${session.title}`}
      subtitle={`${fmtTime(session.startAt)}${session.endAt ? ` – ${fmtTime(session.endAt)}` : ''} · hold each badge in front of the camera, nothing to press`}
      footer={<><span style={{ marginRight: 'auto', fontSize: 13, color: 'var(--text-mid)' }}><strong style={{ color: 'var(--gold-300)', fontSize: 18 }}>{marked}</strong> marked present this sitting</span><Button onClick={onClose}>Done</Button></>}>
      {asking ? (
        <div style={{ padding: 16, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)' }}>
          <p style={{ fontSize: 15, fontWeight: 600 }}>{asking.guest?.name}</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
            {Number.isFinite(asking.expected)
              ? `The list says ${asking.expected} ${asking.expected === 1 ? 'person' : 'people'} with them. Enter who actually came.`
              : 'The list says nothing about guests. Enter how many came with them.'}
          </p>
          <Field label="People with them" required>
            <Input type="number" min="0" max={asking.max || undefined} autoFocus value={asking.value}
              onChange={(e) => setAsking({ ...asking, value: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') mark(asking.code, Number(asking.value) || 0); }} />
          </Field>
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <Button variant="subtle" onClick={() => setAsking(null)} disabled={busy}>Skip this badge</Button>
            <Button loading={busy} style={{ flex: 1 }} onClick={() => mark(asking.code, Number(asking.value) || 0)}>
              Mark present
            </Button>
          </div>
        </div>
      ) : (
      <div style={{ position: 'relative' }}>
        <QrScanner onScan={onScan} busy={busy} hint="Point at the attendee's badge" />
        <AnimatePresence>
          {flash && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
              style={{ position: 'absolute', inset: 0, borderRadius: 'var(--r-lg)', display: 'grid', placeItems: 'center', background: flash.ok ? 'rgba(20,83,45,0.92)' : 'rgba(127,29,29,0.92)', pointerEvents: 'none' }}>
              <div style={{ textAlign: 'center', padding: 20 }}>
                {flash.ok ? <CheckCircle2 size={56} color="var(--ok)" /> : <AlertTriangle size={56} color="var(--danger)" />}
                <p style={{ fontSize: 30, fontWeight: 800, marginTop: 8 }}>{flash.title}</p>
                <p style={{ fontSize: 15, marginTop: 4 }}>{flash.sub}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )}
      {feed.length > 0 && (
        <div style={{ marginTop: 12, maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
          {feed.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)', color: f.already ? 'var(--text-lo)' : 'var(--text-hi)' }}>
              <CheckCircle2 size={13} color={f.already ? 'var(--text-lo)' : 'var(--ok)'} />
              <span style={{ flex: 1 }}>{f.name}</span>
              {f.companions > 0 && (
                <Badge tone={f.state === 'up' ? 'warning' : 'success'}>+{f.companions}</Badge>
              )}
              <span style={{ color: 'var(--text-lo)' }}>{f.already ? 'already' : fmtTime(f.at)}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
